import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { fetchAddressAreaByPin, updateAddressSubArea } from "../../service/AddressApi";
import "../general/General.css";
import { useParams } from "react-router-dom";
import { useCheckAccess } from "../../utils/Utilities";
import { Loading, Navbar, Footer, Breadcrumbs } from "../../components/shared";
import { Helmet } from "react-helmet-async";
import { translateText } from "../../service/UtilApi"; // Import the function


const AddressSubAreaUpdate = () => {
  useCheckAccess('AddressRW'); // Check if the user is logged in
  const [addressAreaData, setAddressAreaData] = useState({ addressAreaList: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedArea, setSelectedArea] = useState(null);
  const { pincode } = useParams();
  const [subAreaList, setSubAreaList] = useState([]); // Track multiple sub-areas
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();

  // Fetch address area data by pin
  useEffect(() => {
    const fetchData = async () => {
      if (pincode) {
        try {
          setLoading(true);
          const data = await fetchAddressAreaByPin(pincode, 'false'); // Fetch the data
          setAddressAreaData(data || { addressAreaList: [] }); // Handle response data or set an empty array
        } catch (err) {
          setError("Failed to fetch address data.");
          console.error(err); // Log error for debugging
        } finally {
          setLoading(false);
        }
      } else {
        setError("Invalid PIN received");
        setLoading(false);
      }
    };

    fetchData();
  }, [pincode]);

  const handleFormSubmit = async () => {
    if (!selectedArea?.id) {
      setError("Please select an area before submitting.");
      return; // Prevent submission if no area is selected
    }
    setLoading(true);
    const payload = {
      id: selectedArea.id,  // Ensure selectedArea.id is present
      subAreas: subAreaList,  // Include the list of sub-areas
    };
    try {
      const success = await updateAddressSubArea(payload);
      if (success) {
        navigate(`/address-area/view/${pincode}`);
      }
    } catch (error) {
      console.error("Error updating address area:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTranslateSubArea = async () => {
    try {
      const translatedSubAreaName = await translateText(watch("subAreaName"));
      setValue("subAreaNameTamil", translatedSubAreaName); // Set the translated name in Tamil field
    } catch (error) {
      console.error("Error translating sub-area name:", error);
      setError("Failed to translate sub-area name. Please try again.");
    }
  };

  // Handle area selection
  const handleAreaSelect = (e) => {
    const selectedId = e.target.value;
    const selected = addressAreaData.addressAreaList.find(area => area.id === selectedId);

    if (selected) {
      setSelectedArea(selected);  // Update the selected area state
      setSubAreaList(selected.subAreas || []);  // Use subAreas from the selected area
    } else {
      setSelectedArea(null);  // Clear selected area if none is found
      setSubAreaList([]);  // Reset sub-area list
    }
    setError(""); // Clear error on selection change
  };

  // Add new sub-area to the list
  const handleAddSubArea = () => {
    const name = watch("subAreaName");
    const nameTamil = watch("subAreaNameTamil");
    if (name && nameTamil && !subAreaList.some(subArea => subArea.name.name === name && subArea.name.name_tamil === nameTamil)) {
      setSubAreaList(prevList => [
        ...prevList,
        {
          id: String(prevList.length + 1), // Generate ID for new sub-area
          name,
          name_tamil: nameTamil, // Add both name and name_tamil as an object
        },
      ]);
      setValue("subAreaName", ""); // Clear input field after adding
      setValue("subAreaNameTamil", ""); // Clear input field after adding
    }
  };

  // Remove sub-area from the list
  const handleRemoveSubArea = (index) => {
    setSubAreaList(prevList => prevList.filter((_, idx) => idx !== index));
  };

  // Display loading or error states
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              <Breadcrumbs /><br />
              <Loading />
            </div>
          </div>
        </div>
        <Footer />
      </>

    );
  }

  if (error) {
    return (
      <Loading />
    );
  }

  return (
    <>
      <Navbar />
      <Helmet>
        <title>அஞ்சல் புதுப்பிக்கவும்</title>
        <link rel="canonical" href="/address-area/update" />
      </Helmet>

      <div className="general_parent">
        <div className="general_body">
          <Breadcrumbs /><br />
          <h3>பின்னுக்கான அஞ்சல் பட்டியல்: {pincode}</h3>
        </div>
      </div>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <button onClick={() => navigate("/address/admin")}>முகவரி நிர்வாகி பக்கம்</button>
            <br />
            <button onClick={() => navigate("/address-area/create")}>அஞ்சலை உருவாக்கு</button>
          </div>
        </div>
      </div>

      {addressAreaData.addressAreaList && addressAreaData.addressAreaList.length > 0 ? (
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              {/* Dropdown to select Area */}
              <h3>அஞ்சல் தேர்ந்தெடுக்கவும்</h3>

              <br />

              <select onChange={handleAreaSelect} value={selectedArea?.id || ""}
                className="auth_select"
              >
                <option value="">அஞ்சல் தேர்ந்தெடுக்கவும்</option>
                {addressAreaData.addressAreaList.map((addressArea) => (
                  <option key={addressArea.id} value={addressArea.id}>
                    {addressArea.name},{addressArea.block},{addressArea.district}, {addressArea.state} ({addressArea.name_tamil},{addressArea.block_tamil}, {addressArea.district_tamil})
                  </option>
                ))}
              </select>

              {/* Display existing Sub-Areas */}
              {selectedArea && (
                <>
                  <br />
                  <h3>{selectedArea.name} ({selectedArea.name_tamil})</h3>
                  <ul>
                    {subAreaList.length > 0 ? (
                      subAreaList.map((subArea, index) => (
                        <li key={index}>
                          <hr></hr>
                          {/* Access subArea.name.name and subArea.name.name_tamil */}
                          <div>
                            {'ஊர்:'}{subArea.name.split('|').reverse().map((token, i) => (
                              <div key={i}>{token}</div>
                            ))}
                            {'அஞ்சல்: '}{selectedArea.name}
                            <br />
                            {'தாலுக்கா: '}{selectedArea.block}
                            <br />
                            {'மாவட்டம்: '}{selectedArea.district}
                            <br />
                            {selectedArea.state}
                            <br />
                            {selectedArea.pincode}
                          </div>

                          {/* Tokenize and reverse subArea.name.name_tamil */}
                          <div>
                            {'ஊர்:'}{subArea.name_tamil.split('|').reverse().map((token, i) => (
                              <div key={i}>{token}</div>
                            ))}
                            {'அஞ்சல்: '}{selectedArea.name_tamil || "NA"}
                            <br />
                            {'தாலுக்கா: '}{selectedArea.block_tamil || "NA"}
                            <br />
                            {'மாவட்டம்: '}{selectedArea.district_tamil || "NA"}
                            <br />
                            {selectedArea.state_tamil || "NA"}
                            <br />
                            {selectedArea.pincode}
                          </div>
                          <br />

                          <button
                            type="button"
                            onClick={() => handleRemoveSubArea(index)}
                            style={{ marginLeft: "10px" }}
                          >
                            நீக்கு
                          </button>

                        </li>
                      ))
                    ) : (
                      <p>இந்தப் அஞ்சலுக்கு ஊர் எதுவும் கிடைக்கவில்லை..</p>
                    )}
                  </ul>

                  {/* Form to Add New Sub-Area */}
                  <hr></hr>
                  <form onSubmit={handleSubmit(handleFormSubmit)} className="auth_form" autoComplete="off">
                    <h3>ஊர் சேர்க்கவும்</h3>

                    <div className="auth_element">
                      <label className="auth_label">ஊர் (English) Eg. Vettamangalam(Big)|Velliyampalayam(Small)</label>
                      <input
                        type="text"
                        id="subAreaName"
                        {...register("subAreaName", {
                          // required: "Sub-Area Name (English) is required",
                          pattern: {
                            //  value: /^[A-Za-z\s]*$/, // English letters and spaces only
                            message: "Only alphabets and spaces are allowed in Sub-Area Name",
                          },
                        })}
                        className="auth_input"
                        placeholder="Enter Sub-Area Name (English)"
                      />
                      {errors.subAreaName && <div className="auth_error">{errors.subAreaName.message}</div>}
                    </div>
                    {/* button to Translate Sub-Area Name */}
                    <button
                      type="button"
                      onClick={handleTranslateSubArea}
                      className="auth_button"
                      disabled={loading || !watch("subAreaName")}
                    >
                      Translate Sub-Area Name to Tamil
                    </button>
                    <div className="auth_element">
                      <label className="auth_label">ஊர் (Tamil)</label>
                      <input
                        type="text"
                        id="subAreaNameTamil"
                        {...register("subAreaNameTamil", {
                          //required: "Sub-Area Name (Tamil) is required",
                          pattern: {
                            // value: /^[\u0B80-\u0BFF\s]*$/, // Tamil characters and spaces only
                            message: "Only Tamil characters and spaces are allowed",
                          },
                        })}
                        className="auth_input"
                        placeholder="Enter Sub-Area Name (Tamil)"
                      />
                      {errors.subAreaNameTamil && <div className="auth_error">{errors.subAreaNameTamil.message}</div>}
                    </div>

                    <button
                      type="button"
                      onClick={handleAddSubArea}
                      className="auth_submit"
                      disabled={loading || !watch("subAreaName") || !watch("subAreaNameTamil")}
                    >
                      ஊர் சேர்க்கவும்
                    </button>

                    <button type="submit" className="auth_submit">
                      சமர்ப்பிக்கவும்
                    </button>
                  </form>

                </>
              )} <br />
              <hr></hr>

            </div>
          </div>
        </div>

      ) : (
        <div>கொடுக்கப்பட்ட பின்கோடுக்கு எந்த அஞ்சலும் கிடைக்கவில்லை</div>
      )}

      <br /><br />
      <Footer />
    </>
  );
};
export default AddressSubAreaUpdate;